import React from 'react';
import Layout from '../Layout';
import { colors } from '../../colors';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';

const UserGuide = () => {
    return (
        <Layout>
            {/* Blue Banner */}
            <Box
                sx={{
                    backgroundColor: colors.primaryBannerBlue,
                    color: 'white',
                    padding: { xs: '5px 0', sm: '10px 0' },
                    textAlign: 'center',
                    fontSize: { xs: '1rem', sm: '1.2rem' },
                    fontWeight: '500',
                }}
            >
                User Guide
            </Box>
            <motion.div initial="initial" animate="animate" transition={{ duration: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        width: '100%',
                        minHeight: '100vh',
                        backgroundColor: '#eaecee',
                        padding: { xs: '10px', sm: '20px', md: '20px' },
                        boxSizing: 'border-box',
                        gap: { xs: 2, sm: 3 },
                    }}
                >
                    {/* Main Guide Content */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            padding: '20px',
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            boxShadow: '0 6px 14px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        {/* Introduction */}
                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Welcome to the Admin Template Management Page
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            This guide will walk you through the features and capabilities of the Template Management page.
                            You can use this page to create, modify, and upload survey templates, which are essential for managing survey forms.
                        </Typography>

                        {/* Template Overview */}
                        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                            Overview of Template Features
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Templates consist of sections, each containing various types of questions such as input fields, multiple-choice questions,
                            sliders, dropdowns, and tables. You can create a new template from scratch or upload an Excel-based template for easy management.
                        </Typography>

                        {/* Creating a Template */}
                        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                            Creating a Template
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            To create a template, follow these steps:
                        </Typography>
                        <ol style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                            <li>
                                <Typography variant="body1">
                                    Click the <strong>Add Section</strong> button to add a new section.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body1">
                                    Use the <strong>Add Question</strong> options to add questions to each section. You can select from different question types like text input, single choice (SCQ), dropdown, and more.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body1">
                                    Use the <strong>Edit</strong> button to modify section titles or question details. To rearrange sections, you can use the <strong>Up</strong> and <strong>Down</strong> arrows.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body1">
                                    Save your template by clicking the <strong>Save Template</strong> button.
                                </Typography>
                            </li>
                        </ol>

                        {/* Uploading a Template */}
                        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                            Uploading a Template
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            To upload a template:
                        </Typography>
                        <ol style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                            <li>
                                <Typography variant="body1">
                                    Prepare your template in the required Excel format, ensuring that it contains sections, question types, and options.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body1">
                                    Navigate to the <strong>Upload Template</strong> option.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body1">
                                    Select the Excel file and click <strong>Upload</strong>. The system will parse the file and display the sections and questions.
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body1">
                                    Verify the uploaded content and save the template.
                                </Typography>
                            </li>
                        </ol>

                        {/* Managing Sections and Questions */}
                        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                            Managing Sections and Questions
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Once a section is active, you can add, edit, or delete questions. If you wish to delete a section, ensure that it is not currently active for editing.
                            The app prevents deleting an active section to avoid potential data conflicts.
                        </Typography>

                        {/* Conditional Logic */}
                        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                            Applying Conditional Logic
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            You can apply conditions to questions using the <strong>Link</strong> icon. When adding a condition, you will be able to choose from
                            other questions within the same section and specify the expected answer.
                        </Typography>

                        {/* Summary */}
                        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                            Summary
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            This user guide provides the necessary steps to create, manage, and upload survey templates. By following these steps, you can efficiently organize surveys for your platform.
                        </Typography>
                    </Box>
                </Box>
            </motion.div>
        </Layout>
    );
};

export default UserGuide;
